<template>
  <ModalDialog
    ref="dialog"
    title="Scan Orders"
    :submitTitle="submitTitle"
    :submitting="submitting"
    :submitDisabled="submitDisabled"
    icon="fa-truck"
    closeTitle="Cancel"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <div>
      <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
        <transition name="fade">
          <NotificationBar v-if="scanResult.message" :type="scanResult.type">
            <NotificationBarTitle>{{scanResult.message}}</NotificationBarTitle>
          </NotificationBar>
        </transition>
        <FormRow label="Processing Type">
          <FormOption label="Production" name="processType" setting="production" v-model="settings.processType" inline="true" />
          <FormOption label="Despatch" name="processType" setting="despatch" v-model="settings.processType" inline="true" />
        </FormRow>
        <FormRow label="Barcode Scan">
          <FormInputText ref="scanBarcodeEntry" v-model="scanning.barcode" field="barcode" @enter="onBarcodeEntered" note="Use a barcode scanner or manually enter the order barcode & press enter" />
        </FormRow>
        <div v-show="isProcessingTypeDespatch">
          <FormRow label="Force Complete Production" v-if="isForceProductionAvailable" helpTip="If set, will force orders in production status to be completed">
            <FormCheckbox v-model="settings.forceCompleteProduction" />
          </FormRow>
          <FormRow label="Force Skip COD" v-if="isForceProductionAvailable && settings.forceCompleteProduction" helpTip="If set, will skip pending COD after completing production if applicable">
            <FormCheckbox v-model="settings.forceSkipCOD" />
          </FormRow>
          <FormRow label="Status">
            <FormStaticLabel>
              <div :class="{'result-fg-success': availableCount > 0}">
                {{availableCount}} order{{availableCount | displayCountPlural}} ready for despatch
              </div>
              <div v-if="errorCount > 0" class="result-fg-failed">
                <strong>Warning:</strong> {{errorCount}} order{{errorCount | displayCountPlural}} have errors and cannot be despatched
              </div>
            </FormStaticLabel>
          </FormRow>
        </div>
        <div v-show="isProcessingTypeProduction">
          <FormRow label="Status">
            <FormStaticLabel>
              <div :class="{'result-fg-success': availableCount > 0}">
                {{availableCount}} order{{availableCount | displayCountPlural}} ready to complete production
              </div>
              <div v-if="errorCount > 0" class="result-fg-failed">
                <strong>Warning:</strong> {{errorCount}} order{{errorCount | displayCountPlural}} have errors and cannot have production completed
              </div>
            </FormStaticLabel>
          </FormRow>
        </div>
        
        <div class="mt-2"> </div>
        <ContentTitle>Orders Scanned</ContentTitle>
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th>Order Num</th>
              <th>Barcode</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Status</th>
              <th>Despatch Info</th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <template v-for="order in orderList">
              <tr :key="order.id">
                <td class="font-weight-bold">
                  <LinkOrder :id="order.id" :name="order.sequenceNumber" />
                </td>
                <td>{{order.barcode}}</td>
                <td>{{order.contactName}}</td>
                <td>
                  <LabelBadge :labelClass="`label-order-type-${order.type}`">{{order.type | formatLookupName(OrderTypeLookup)}}</LabelBadge>
                </td>
                <td>
                  <LabelBadge :labelClass="`label-order-status-${order.status}`">{{order.status | formatLookupName(OrderStatusTypeLookup)}}</LabelBadge>
                  <LabelBadge labelClass="label-order-hold ml-1" v-if="order.isOnHold">Hold</LabelBadge>                                
                </td>
                <td>
                  <span v-if="order.hasError" class="font-weight-bold result-fg-failed">{{order.error}}</span>
                  <span v-if="!order.hasError && isProcessingTypeDespatch" class="result-fg-success">Available for Despatch</span>
                  <span v-if="!order.hasError && isProcessingTypeProduction" class="result-fg-success">Available to complete production</span>
                  <span v-if="order.orderPaymentType == OrderPaymentTypes.cod && !order.hasError && isProcessingTypeDespatch"> (COD Required)</span>
                </td>
                <td>
                  <ActionDropMenu :tableAction="true">
                    <DropdownMenuItem icon="fa-trash-alt" @click="removeOrder(order)">Remove Order</DropdownMenuItem>
                  </ActionDropMenu>
                </td>
              </tr>
            </template>
          </template>
          <template v-slot:empty v-if="orderList.length == 0">
            Order list is empty
          </template>
        </StaticDataTable>
      </FormGroup>
    </div>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import StaticDataTable from '@/components/table/staticDataTable.vue'

import { OrderTypes, OrderTypeLookup, OrderStatusTypes, OrderStatusTypeLookup, AccountSettingKeys, OrderPaymentTypes } from '@/store/types'
import FormCheckbox from '@/components/form/formCheckbox.vue'

export default {
  name: 'ScanOrdersDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    StaticDataTable,
    FormCheckbox
  },
  data() {
    return {
      dialogForm: {},
      scanning: {
        barcode: '',
      },
      settings: {
        processType: 'production',
        forceCompleteProduction: false,
        forceSkipCOD: false
      },
      scanResult: {
        message: '',
        type: ''
      },
      loadingOrder: false,
      submitting: false,
      orderList: []
    }
  },
  created() {
    this.OrderTypeLookup = OrderTypeLookup
    this.OrderStatusTypeLookup = OrderStatusTypeLookup
    this.OrderPaymentTypes = OrderPaymentTypes
  },
  mounted() {
    this.focusBarcode()
  },
  watch: {
    isForceProductionAvailable: {
      immediate: true,
      handler: function() {
        // Default the setting to on
        if(this.isForceProductionAvailable && !this.settings.forceCompleteProduction) {
          this.settings.forceCompleteProduction = true
          this.settings.forceSkipCOD = true
        }
      }
    },
    'settings.processType': function() {
      this.clearResultMessage()
      this.clearOrders()
    }
  },
  computed: {
    ...mapState({
      accountSettings: state => state.account.accountSettings
    }),
    errorCount() {
      return this.orderList.filter(order => order.hasError).length
    },
    availableCount() {
      return this.orderList.filter(order => !order.hasError).length
    },
    isProcessingTypeDespatch() {
      return this.settings.processType == 'despatch'
    },
    isProcessingTypeProduction() {
      return this.settings.processType == 'production'
    },
    submitTitle() {
      if(this.isProcessingTypeDespatch)
        return 'Despatch Orders'

      return 'Complete Production'
    },
    submitDisabled() {
      if(this.orderList.length == 0)
        return true

      if(this.errorCount > 0)
        return true
      return false
    },
    isForceProductionAvailable() {
      return parseInt(this.accountSettings[AccountSettingKeys.orderDespatchScanForceProductionEnabled] || 0) != 0
    }
  },
  methods: {
    show() {
      this.dialogForm = { }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      let scanDialog = this
      if(this.availableCount > 0 && this.errorCount == 0) {
        let message = `This wil flag <strong>${this.availableCount}</strong> order(s) as despatched and will generate <strong>invoices</strong> if applicable`
        if(this.isProcessingTypeProduction)
          message = `This wil flag <strong>${this.availableCount}</strong> order(s) as completed production`

        this.confirmMessage('Confirm Process Orders?', '', { html: message})
        .then(success => {
          if(success) {

            // Submit the process orders request
            this._submitOrderList()
          } else {

            // Focus after a timeout to let msgbox close with animations
            setTimeout(() => {
              scanDialog.focusBarcode()
            }, 500)
          }
        })        
      }
    },
    async _submitOrderList() {
      this.submitting = true

      this.dialogForm = this.clearFormResponse()
      this.clearResultMessage()

      var orderIdList = this.orderList.map(order => order.id)
      
      var batchSize = 1     // Do one at a time to avoid issues with large transactions on server
      var orderChunkList = _.chunk(orderIdList, batchSize)

      // Break into submitting into chunks to process better with UX
      var submitSuccess = true
      for(var orderIdGroup of orderChunkList) {
        try {
          await this.$apiService.ordersService.orderForceManualScanList({
            orderIdList: orderIdGroup,
            forceCompleteProduction: this.isProcessingTypeProduction || this.settings.forceCompleteProduction,
            forceCompleteDespatch: this.isProcessingTypeDespatch,
            forceSkipCOD: this.isProcessingTypeDespatch && this.settings.forceSkipCOD
          })

          // Remove orders from the list
          this.orderList = this.orderList.filter(order => orderIdGroup.indexOf(order.id) != -1)

        } catch(err) {
          this.dialogForm = this.processFormResponse(err)
          this.focusBarcode()
          submitSuccess = false
          break
        }
      }

      this.submitting = false
      if(submitSuccess) {
        this.modalClosed(true)
        this.hide()
      }
    },
    onBarcodeEntered(ev) {
      if(ev)
        ev.preventDefault()
      
      this.dialogForm = this.clearFormResponse()

      // Reset
      this.clearResultMessage()
      this.loadingOrder = true

      this.$apiService.ordersService.runOrderBarcodeLookup(this.scanning.barcode)
      .then(response => {
        let order = response.data

        var errorMessage = ''

        if(this.isProcessingTypeDespatch) {
          if(order.status == OrderStatusTypes.production) {
            if(!this.settings.forceCompleteProduction)
              errorMessage = 'Still in production'
          }
          else if(order.status == OrderStatusTypes.pendingCODPaid) {
            if(!this.settings.forceSkipCOD || !this.settings.forceCompleteProduction)
              errorMessage = 'Pending COD payments'
          }
          else if(order.status != OrderStatusTypes.despatch)
            errorMessage = 'Not in despatchable state!'
        }
        if(this.isProcessingTypeProduction) {
          if(order.status != OrderStatusTypes.production) {
            errorMessage = 'Not in production'
          }            
        }

        if(order.type == OrderTypes.quote)
          errorMessage = 'Order is a quote'

        if(order.isOnHold)
          errorMessage += ' On Hold'

        var listOrderInfo = {
          id: order.id,
          type: order.type,
          barcode: order.barcode,
          sequenceNumber: order.sequenceNumber,
          status: order.status,
          isOnHold: order.isOnHold,
          orderPaymentType: order.orderPaymentType,
          contactName: order.contact ? order.contact.name : '',
          hasError: errorMessage != '',
          error: errorMessage
        }

        // Ignore if already in the list
        var existingOrderIndex = this.orderList.findIndex(curOrder => curOrder.id == order.id)
        if(existingOrderIndex != -1) {
          this.setResultMessage(`Order ${order.sequenceNumber} already in the list! Order updated`, 'warning')

          // Update the status details
          // In case the order was fixed & a re-scan will cause it to be reupdated
          this.orderList[existingOrderIndex] = listOrderInfo
          return
        }

        this.orderList.push(listOrderInfo)

        if(errorMessage != '')
          this.setResultMessage(`Order ${order.sequenceNumber} added to the list with errors`, 'error')
        else
          this.setResultMessage(`Order ${order.sequenceNumber} added to the list`, 'success')
      })
      .catch(err => {
        this.setResultMessage(this.processFormResponse(err).errorMessage, 'error')
      })
      .finally(() => {
        this.scanning.barcode = ''

        this.focusBarcode()
      })
    },

    clearOrders() {
      this.orderList = []
    },

    clearResultMessage() {
      this.scanResult.message = ''
      this.scanResult.type = ''
    },

    setResultMessage(message, type) {
      this.scanResult.message = message
      this.scanResult.type = type
    },

    removeOrder(order) {
      this.orderList = this.orderList.filter(listOrder => listOrder.id != order.id)
      this.setResultMessage(`Order ${order.sequenceNumber} removed from the list`, 'warning')
      this.focusBarcode()
    },
    focusBarcode() {
      this.$nextTick(() => {
        this.$refs.scanBarcodeEntry.focus()
        this.$refs.scanBarcodeEntry.select()
      })
    }
  }
}
</script>